<template>
  <section
    v-if="hasContent"
    v-reveal
    :id="anchorId"
    class="v2-precision"
  >
    <!-- Layered bg: cool blue/teal gradient + subtle overlay -->
    <div class="v2-precision__bg-primary" aria-hidden="true"></div>
    <div class="v2-precision__bg-mesh" aria-hidden="true"></div>

    <div class="v2-precision__container">
      <div class="v2-precision__header">
        <span
          v-if="precisionAnalysis.eyebrow"
          class="v2-precision__eyebrow"
        >
          {{ precisionAnalysis.eyebrow }}
        </span>
        <h2 class="v2-precision__title">{{ precisionAnalysis.headline }}</h2>
        <div
          v-if="precisionAnalysis.intro"
          class="v2-precision__intro"
          v-html="precisionAnalysis.intro"
        ></div>
      </div>

      <!-- 3 cards + enrollment card, side-by-side on desktop -->
      <div class="v2-precision__layout">
        <div class="v2-precision__cards">
          <div
            v-for="col in columns"
            :key="col.key"
            class="v2-precision__card"
          >
            <div class="v2-precision__card-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                v-html="iconSvg(col.key)"
              />
            </div>
            <h3 class="v2-precision__card-title">{{ col.heading }}</h3>
            <ul class="v2-precision__card-list">
              <li
                v-for="(item, i) in col.items"
                :key="i"
                class="v2-precision__card-item"
              >
                <span class="v2-precision__bullet" aria-hidden="true">•</span>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Enrollment CTA card (navy, right-side fixed 340px) -->
        <aside
          v-if="hasEnrollmentCta"
          class="v2-precision__enroll"
        >
          <h3 class="v2-precision__enroll-title">{{ enrollmentCta.headline || 'Are You a Patient or Caregiver?' }}</h3>
          <div
            v-if="enrollmentCta.body"
            class="v2-precision__enroll-body"
            v-html="enrollmentCta.body"
          ></div>
          <div
            v-if="enrollmentBenefits.length > 0"
            class="v2-precision__enroll-benefits"
          >
            <div
              v-for="(benefit, i) in enrollmentBenefits"
              :key="benefit"
              class="v2-precision__enroll-benefit"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="v2-precision__enroll-benefit-icon"
                aria-hidden="true"
                v-html="benefitIconSvg(i)"
              />
              <span>{{ benefit }}</span>
            </div>
          </div>
          <!-- Always opens the in-page V2PrecisionEnrollmentModal via the
               global mitt emitter. The legacy `enrollment_cta.button_link`
               column is intentionally ignored on the public page now —
               the modal is the canonical enrollment flow. The admin
               editor demotes that field to a hidden legacy column in a
               follow-up edit. -->
          <button
            type="button"
            class="v2-precision__enroll-btn"
            @click="openEnrollmentModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-precision__enroll-btn-icon"
              aria-hidden="true"
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
            {{ enrollmentCta.button_label || 'Request Information' }}
          </button>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
// Lucide-exact icon markup (as raw inner-SVG children strings, since
// we render with v-html into the <svg> wrapper).
// - Microscope for "What You Get"
// - FileText for "How It Works"
// - ShieldCheck for "Who Qualifies"
const CARD_ICON_SVG = {
  what_you_get:
    '<path d="M6 18h8"/>' +
    '<path d="M3 22h18"/>' +
    '<path d="M14 22a7 7 0 1 0 0-14h-1"/>' +
    '<path d="M9 14h2"/>' +
    '<path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z"/>' +
    '<path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"/>',
  how_it_works:
    '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>' +
    '<polyline points="14 2 14 8 20 8"/>' +
    '<line x1="16" y1="13" x2="8" y2="13"/>' +
    '<line x1="16" y1="17" x2="8" y2="17"/>' +
    '<line x1="10" y1="9" x2="8" y2="9"/>',
  who_qualifies:
    '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>' +
    '<polyline points="9 12 12 15 18 9"/>',
}

// Rotating benefit icons: Heart → ShieldCheck → FileText (mirrors Lovable)
const BENEFIT_ICONS_SVG = [
  // Heart
  '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
  // ShieldCheck
  '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>' +
    '<polyline points="9 12 12 15 18 9"/>',
  // FileText
  '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>' +
    '<polyline points="14 2 14 8 20 8"/>' +
    '<line x1="16" y1="13" x2="8" y2="13"/>' +
    '<line x1="16" y1="17" x2="8" y2="17"/>' +
    '<line x1="10" y1="9" x2="8" y2="9"/>',
]

export default {
  name: 'V2PrecisionAnalysis',
  props: {
    precisionAnalysis: { type: Object, default: () => ({}) },
    anchorId: { type: String, default: 'research' },
  },
  computed: {
    hasContent() {
      return this.precisionAnalysis?.headline
        || (this.precisionAnalysis?.what_you_get?.length)
        || (this.precisionAnalysis?.how_it_works?.length)
        || (this.precisionAnalysis?.who_qualifies?.length)
    },
    columns() {
      return [
        { key: 'what_you_get', heading: 'What You Get', items: this.precisionAnalysis?.what_you_get || [] },
        { key: 'how_it_works', heading: 'How It Works', items: this.precisionAnalysis?.how_it_works || [] },
        { key: 'who_qualifies', heading: 'Who Qualifies', items: this.precisionAnalysis?.who_qualifies || [] },
      ].filter(c => c.items.length > 0)
    },
    enrollmentCta() {
      return this.precisionAnalysis?.enrollment_cta || {}
    },
    enrollmentBenefits() {
      return Array.isArray(this.enrollmentCta.benefits) ? this.enrollmentCta.benefits : []
    },
    hasEnrollmentCta() {
      return this.enrollmentCta.headline || this.enrollmentCta.body || this.enrollmentCta.button_link
    },
  },
  methods: {
    iconSvg(key) {
      return CARD_ICON_SVG[key] || ''
    },
    benefitIconSvg(i) {
      return BENEFIT_ICONS_SVG[i % BENEFIT_ICONS_SVG.length] || ''
    },
    /**
     * Emit the global mitt event the parent campaign-page subscribes to.
     * Forwards the admin-edited enrollment_cta payload so the modal can
     * use per-campaign copy (badge, headline, body, benefit pillars)
     * without prop-drilling.
     */
    openEnrollmentModal() {
      if (this.emitter) {
        this.emitter.emit('open-precision-enrollment-modal', {
          content: this.enrollmentCta,
        })
      }
    },
  },
}
</script>

<style scoped>
.v2-precision {
  position: relative;
  overflow: hidden;
  padding-top: 64px;
  padding-bottom: 64px;
}
@media (min-width: 1024px) {
  .v2-precision { padding-top: 96px; padding-bottom: 96px; }
}

/* Cool light blue/teal gradient base */
.v2-precision__bg-primary {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(
    to bottom right,
    hsl(200, 80%, 97%) 0%,
    hsl(195, 70%, 94%) 50%,
    hsl(205, 75%, 96%) 100%
  );
}
.v2-precision__bg-mesh {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to top right,
    hsla(200, 90%, 88%, 0.15) 0%,
    transparent 50%,
    hsla(190, 80%, 85%, 0.15) 100%
  );
}

.v2-precision__container {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin-inline: auto;
  padding-inline: 24px;
}
@media (min-width: 768px)  { .v2-precision__container { padding-inline: 48px; } }
@media (min-width: 1024px) { .v2-precision__container { padding-inline: 64px; } }

/* Header */
.v2-precision__header {
  text-align: center;
  margin-bottom: 56px;
}
.v2-precision__eyebrow {
  display: inline-block;
  color: var(--sideout-pink);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  line-height: 24px;
}
.v2-precision__title {
  margin-top: 12px;
  color: var(--sideout-navy);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  text-wrap: balance;
}
@media (min-width: 768px) {
  .v2-precision__title { font-size: 36px; }
}
.v2-precision__intro {
  margin-top: 16px;
  max-width: 42rem; /* max-w-2xl */
  margin-inline: auto;
  color: var(--sideout-muted);
  font-size: 18px;
  line-height: 1.625;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-precision__intro :deep(p) {
  margin: 0;
}
.v2-precision__intro :deep(p + p) {
  margin-top: 16px;
}
.v2-precision__intro :deep(strong) {
  color: var(--sideout-navy);
  font-weight: 700;
}

/* Layout: 3 cards + enrollment aside.
   Using CSS grid (not flex) for the outer layout so both columns have
   a DEFINITE height (max of the two column contents). Flex with
   align-items: stretch works for siblings, but an inner grid with
   grid-auto-rows:1fr inside a flex item needs a definite container
   height for 1fr to behave as expected — switching the outer to grid
   guarantees that. */
.v2-precision__layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
}
@media (min-width: 1024px) {
  .v2-precision__layout {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 40px;
    /* align-items defaults to stretch in grid — both columns share
       the height of the tallest one, creating equal-height visuals
       regardless of which side has more content. */
  }
}

/* 3 column cards. `grid-auto-rows: 1fr` forces the single row to fill
   the stretched flex-row height so each card matches the enrollment
   card's height. Without it, the row sizes to the tallest card's
   content, leaving empty space below on taller layouts and creating
   a visible height mismatch with the navy enrollment card. */
.v2-precision__cards {
  display: grid;
  gap: 24px;
  grid-auto-rows: 1fr;
}
@media (min-width: 640px) {
  .v2-precision__cards { grid-template-columns: 1fr 1fr 1fr; }
}

.v2-precision__card {
  background: #ffffff;
  border: 1px solid var(--sideout-border);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-precision__card-icon {
  width: 64px;
  height: 64px;
  border-radius: 9999px;
  background: rgba(0, 156, 222, 0.1); /* brand blue @ 10% */
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #009cde; /* Brand blue — icons in blue, not pink */
}
.v2-precision__card-icon svg {
  width: 32px;
  height: 32px;
  stroke: currentColor;
}
.v2-precision__card-title {
  color: var(--sideout-navy);
  font-size: 18px;
  line-height: 1.4;
  font-weight: 700;
  margin-bottom: 12px;
}
.v2-precision__card-list {
  list-style: none;
  padding: 0;
  /* Left-justify the bullet text, but keep the list a tidy block centered
     under the centered card icon/title. fit-content shrink-wraps the list
     (clamped to the card width for long copy) so margin-inline:auto can
     center it; the text inside stays flush-left. */
  margin: 0 auto;
  width: fit-content;
  max-width: 100%;
  text-align: left;
}
.v2-precision__card-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--sideout-muted);
}
.v2-precision__card-item + .v2-precision__card-item {
  margin-top: 8px;
}
.v2-precision__bullet {
  flex-shrink: 0;
  font-weight: 700;
  margin-top: 1px;
  color: #009cde;
  line-height: 1.5;
}

/* Enrollment CTA card (navy) */
.v2-precision__enroll {
  background: var(--sideout-navy);
  color: #ffffff;
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-precision__enroll-title {
  color: #ffffff;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 800;
  margin: 0 0 8px;
}
.v2-precision__enroll-body {
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  line-height: 1.35;
  margin-bottom: 20px;
}
.v2-precision__enroll-body :deep(p) { margin: 0; }
.v2-precision__enroll-body :deep(p + p) { margin-top: 12px; }
.v2-precision__enroll-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  /* Left-justify the benefit rows (icons line up in a column) while the whole
     group stays centered under the centered title. The enroll card is a flex
     column with align-items:center, so a shrink-wrapped (auto-width) benefits
     block is centered automatically. */
  align-items: flex-start;
}
.v2-precision__enroll-benefit {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.2;
}
.v2-precision__enroll-benefit-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: var(--sideout-pink); /* Pink icon on navy bg */
  stroke: currentColor;
}
.v2-precision__enroll-btn {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background-color: #009cde; /* Brand blue */
  color: #ffffff;
  padding: 12px 24px;
  border: none;
  border-radius: 9999px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s, filter 0.15s;
}
.v2-precision__enroll-btn:hover {
  opacity: 0.9;
  filter: brightness(1.05);
}
.v2-precision__enroll-btn:active {
  transform: scale(0.98);
}
.v2-precision__enroll-btn-icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  flex-shrink: 0;
}

/* Mobile font bump — client feedback 2026-05-29 (surgical). Two genuinely-
   small spots: the white-card bullet items sit below the 16px floor, and the
   navy enrollment-card body reads dim (16px @ 70% white on navy). Nudge both
   up at the phone breakpoint. Headings, intro (18), card titles (18),
   enrollment title (24), benefit labels + button (16, fine) left as-is. */
@media (max-width: 640px) {
  .v2-precision__card-item   { font-size: 17px; } /* 15 → 17 */
  .v2-precision__enroll-body { font-size: 18px; } /* 16 → 18 */
}

@media (prefers-reduced-motion: reduce) {
  .v2-precision__enroll-btn {
    transition-duration: 60ms;
  }
  .v2-precision__enroll-btn:active {
    transform: none;
  }
}

/* Mobile horizontal-scroll guard: the analysis grids can resolve a few
   pixels wider than the viewport at narrow widths (grid track min-content +
   the band's own padding), and with overflow visible that spill widened the
   whole document. `clip` contains it at the band edge without creating a
   scroll container (so sticky/absolute descendants are unaffected). */
.v2-precision {
  overflow-x: clip;
}
</style>
